import type { FC } from 'react';
import React, { memo, useEffect, useRef } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { ProgressBarContainer } from '@confluence/global-progress-bar-container/entry-points/ProgressBarContainer.ts';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { resumeLowPriorityEvents } from '@confluence/analytics-event-delay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import {
	getHydrationErrorWithRoute,
	getHydrationFailureOperationalEvent,
} from '@confluence/ssr-utilities/entry-points/matchHydrationError.ts';
import { expVal } from '@confluence/feature-experiments';

import { browserMetricsState } from '../utils/index.ts';
import type { Props } from '../Bm3PageLoadEnd.tsx';

import { addCustomTimingForMetric } from './addCustomTimingsFromMarks.ts';

type UFOLoadEndImplProps = Props & { onComplete: () => void };

const UFOLoadEndImpl: FC<UFOLoadEndImplProps> = memo(({ metric, customData = {}, onComplete }) => {
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		hasFiredRef.current = true;

		if (metric) {
			addCustomTimingForMetric(metric.key);
		}

		addUFOCustomData({
			...customData,
			previousRoute: browserMetricsState.previousRoute,
			pageLoadInfo: getUniquePageLoadId(),
		});

		void resumeLowPriorityEvents();

		// trigger onComplete regardless of FF - PageLoadEnd can stop the progress bar temporarily,
		// as ProgressBarContainer returns early if the progress bar has already been stopped.
		onComplete();

		// Not adding exhaustive dependencies because we want this to fire only once,
		// despite any changes to `customData` or `onComplete`
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onComplete, metric]);

	return null;
});

const UFOLoadEndComponent: FC<Props> = memo(({ metric, customData }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	if (window?.__SSR_RENDERED__ && Boolean(window.__HYDRATION_FAILED__)) {
		const { error: errorWithRoute, route } = getHydrationErrorWithRoute(window.location.toString());
		const attribution = Boolean(window.__SSR_REACT_STREAMING__)
			? 'streaming-hydration'
			: 'hydration';
		if (expVal('confluence_frontend_hydration_failure_reporting', 'isEnabled', false)) {
			createAnalyticsEvent(
				getHydrationFailureOperationalEvent({
					source: `PageLoadEnd - ${metric?.key}`,
					route,
					errorWithRoute,
					isSSRReactStreaming: Boolean(window.__SSR_REACT_STREAMING__),
				}),
			).fire();
		} else {
			getMonitoringClient().submitError(errorWithRoute, {
				attribution,
			});
		}
	}
	return (
		<Subscribe to={[ProgressBarContainer]}>
			{(progressBar: ProgressBarContainer) => (
				<UFOLoadEndImpl metric={metric} customData={customData} onComplete={progressBar.finish} />
			)}
		</Subscribe>
	);
});

export const UFOLoadEnd = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(UFOLoadEndComponent);
